import { Typography } from "@material-ui/core";
import { observer, useObserver } from "mobx-react-lite";
import React, { useEffect } from "react";
import styled from "styled-components";

import { PrimaryButton } from "Components/Buttons/Buttons";
import { ApproveDrawingViewModel } from "./ApproveDrawingViewModel";
import { SnackBar } from "Components/SnackBar/SnackBar";
import { DrawingApprovalPanel } from "./DrawingApprovalPanel";
import { SendForApprovalModal } from "Views/PurchaseOrder/Form/SendForApprovalModal";
import { DashedDivider, SolidDivider } from "Views/PurchaseOrder/Form/Views/Dividers";
import { DarwinInputWrapper } from "Components/Form/DarwinInputWrapper";
import { Files } from "Components/Files/Files/Files";
import { ApproveDrawingModel } from "./ApproveDrawingModel";
import { DownloadDrawingView } from "../../Shared/DownloadDrawingView";
import FolderNavigator from "Components/FolderNavigator/FolderNavigator";
import { DarwinSelect } from "Components/AutoComplete/DarwinSelect";
import { BaseModal } from "Components/Modal/BaseModal";
import { ReportModalStyle } from "Views/Project/Modals/Modal.Styles";

interface IProps {
    viewModel: ApproveDrawingViewModel;
}

const ApproveDrawingViewBase: React.FC<IProps> = observer((props) => {
    const viewModel = props.viewModel;

    useEffect(() => {
        // // Check if the form should be view only.
        // if (viewModel.model.id && AppUrls.Client.Approval.MyAmends === location.pathname) {
        //     viewModel.setIsViewOnly(true);
        // }
    }, []);

    const onSubmit = (event: any) => {
        event.preventDefault();
    };

    const renderButtons = () => {
        return (
            <React.Fragment>
                {viewModel.canAmendDrawing && (
                    <PrimaryButton
                        displayName="Resubmit amendment"
                        execute={() => viewModel.handleShowSendForApprovalModalChange(true)}
                        fullWidth={true}
                        canExecute={!viewModel.isFormDisabled && !viewModel.IsLoading}
                    />
                )}
            </React.Fragment>
        );
    };

    return useObserver(() => {
        return (
            <BaseModal open={viewModel !== null} onClose={viewModel.goBack} title="Drawing Approval" PaperComponent={ReportModalStyle} actions={renderButtons()}>
                {viewModel.showSendForApprovalModal && (
                    <SendForApprovalModal
                        onClose={() => viewModel.handleShowSendForApprovalModalChange(false)}
                        open={viewModel.getShowSendForApprovalModal}
                        title="Send for approval"
                        confirmText="Send for approval"
                        onSave={viewModel.amendDrawing}
                        value={viewModel.model.requesterNotes}
                        setValue={(val: string) => viewModel.setRequesterNote(val)}
                        isLoading={viewModel.IsLoading}
                        isDisabled={!viewModel.getRequesterNoteValid}
                    />
                )}
                <DrawingApprovalPanel
                    viewModel={viewModel}
                    sideOpen={viewModel.approvalPanelViewModel.getCanShowPanel && !viewModel.IsLoading}
                    isFromApprovalSection={true}
                    primaryTitle={viewModel.approvalPanelViewModel.getTitle}
                    secondaryTitle={`${viewModel.approvalPanelViewModel.model.requesterName} ${viewModel.approvalPanelViewModel.getRequestedDateFormatted}`}
                />
                <ContentContainer>
                    <Typography style={{ marginBottom: "8px", font: "normal normal 600 12px/16px Segoe UI", letterSpacing: "-0.18px", color: "#191919" }} variant="h5">
                        Project name: {viewModel.model.projectName}
                    </Typography>
                    <Typography style={{ marginBottom: "8px", font: "normal normal 600 12px/16px Segoe UI", letterSpacing: "-0.18px", color: "#191919" }} variant="h5">
                        Project reference: {viewModel.model.projectReference}
                    </Typography>
                    <Typography style={{ marginBottom: "8px", font: "normal normal 600 12px/16px Segoe UI", letterSpacing: "-0.18px", color: "#191919" }} variant="h5">
                        Output name: {viewModel.model.outputName}
                    </Typography>
                    <DarwinInputWrapper<ApproveDrawingModel>
                        type="text"
                        label="Description:*"
                        placeHolder="Description"
                        validateOnBlur={true}
                        viewModel={viewModel}
                        fieldName="description"
                        shrink={true}
                        editMode={!viewModel.isFormDisabled && !viewModel.IsLoading}
                    />

                    <DarwinInputWrapper<ApproveDrawingModel>
                        type="text"
                        label="Revision:*"
                        placeHolder="Revision"
                        validateOnBlur={true}
                        viewModel={viewModel}
                        fieldName="revisionNumberCustom"
                        shrink={true}
                        editMode={!viewModel.isFormDisabled && !viewModel.IsLoading}
                    />

                    <DarwinSelect
                        displayName="Document source:*"
                        execute={(val: any) => viewModel.setDocumentSource(val.id, true)}
                        fullWidth={true}
                        getOptionLabel={(option: any) => option.displayName || ""}
                        onBlur={() => viewModel.isFieldValid("documentSource")}
                        options={viewModel.documentSourceOptions}
                        placeholder="Please select"
                        validationMessage={viewModel.getError("documentSource")}
                        value={viewModel.documentSourceOption}
                        canExecute={!viewModel.isFormDisabled && !viewModel.IsLoading}
                    />

                    <SolidDivider gutterTop={true} borderOpacity={0} borderThickness={0} />

                    {viewModel.model.autodeskVersionId !== null || viewModel.model.attachmentUrl !== null ? (
                        <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                <DownloadDrawingView
                                    fileName={viewModel.model.name!}
                                    attachmentUrl={viewModel.model.attachmentUrl!}
                                    inlineUrl={viewModel.model.inlineUrl!}
                                    autodeskVersionId={viewModel.model.autodeskVersionId!}
                                    drawingId={viewModel.model.id}
                                />
                                {!viewModel.isFormDisabled && !viewModel.IsLoading && (
                                    <PrimaryButton displayName="Replace Drawing" execute={() => viewModel.replaceDrawing()} fullWidth={false} canExecute={true} />
                                )}
                            </div>
                        </>
                    ) : null}

                    {viewModel.isFromComputer && (
                        <>
                            <DashedDivider gutterTop gutterBottom borderThickness={2} borderOpacity={0.5} />
                            <Files viewModel={viewModel.filesViewModel} disabled={viewModel.IsLoading || !viewModel.canAmendDrawing || viewModel.isFormDisabled} />
                        </>
                    )}

                    {viewModel.isFromCloud &&
                        viewModel.autodeskDocuments.length === 0 &&
                        viewModel.model.autodeskVersionId === null &&
                        viewModel.canAmendDrawing &&
                        !viewModel.isFormDisabled && (
                            <>
                                <DashedDivider gutterTop gutterBottom borderThickness={2} borderOpacity={0.5} />
                                <FolderNavigator
                                    rootFolders={viewModel.rootFolders}
                                    folders={viewModel.folders}
                                    apiLoadCloudFolderContentsAsync={(projectId: string, folderId: string) => viewModel.apiLoadCloudFolderContentsAsync(projectId, folderId)}
                                    apiLoadCloudItemAsync={(projectId: string, itemId: string) => viewModel.apiLoadCloudItemAsync(projectId, itemId)}
                                    isLoading={viewModel.IsLoading}
                                    projectId={viewModel.model.projectId}
                                />
                            </>
                        )}

                    {viewModel.autodeskDocuments.map((i) => {
                        return <div style={{ margin: "15px 0 15px 0" }}>{i.fileName}</div>;
                    })}
                </ContentContainer>
                <SnackBar
                    messageText={viewModel.snackMessage}
                    messageType={viewModel.snackType}
                    active={viewModel.snackbarState}
                    closeOption={() => viewModel.setSnackbarState(false)}
                    autoHideDuration={5000}
                />
            </BaseModal>
        );
    });
});

export const ApproveDrawingView = styled(ApproveDrawingViewBase)`
    display: flex;
    flex: 1;
    flex-direction: column;
`;

const ContentContainer = styled.div`
    max-width: 700px;
    margin: 0 auto;
    width: 100%;
    margin-top: 20px;
`;
